import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import SiteHeader from './components/SiteHeader';
import AdminPage from './pages/AdminPage';
import { api } from './api/client';
import type { Post, PostSummary } from './api/types';
import { useIsAdmin } from './hooks/useIsAdmin';

type View =
  | { type: 'list' }
  | { type: 'post'; slug: string }
  | { type: 'admin' };

function parseHash(): View {
  const hash = window.location.hash;
  if (hash.startsWith('#/post/')) {
    return { type: 'post', slug: hash.slice('#/post/'.length) };
  }
  if (hash === '#/admin') {
    return { type: 'admin' };
  }
  return { type: 'list' };
}

function navigate(hash: string): void {
  window.location.hash = hash;
}

function App() {
  const [view, setView] = useState<View>(parseHash);
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  // Sync view with URL hash
  useEffect(() => {
    const onHash = () => setView(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Fetch post list
  const fetchList = useCallback(() => {
    setListLoading(true);
    api.posts
      .list()
      .then(setPosts)
      .catch(console.error)
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    if (view.type === 'list') fetchList();
  }, [view.type, fetchList]);

  // Fetch individual post
  useEffect(() => {
    if (view.type !== 'post') return;
    setPostLoading(true);
    setSelectedPost(null);
    api.posts
      .get(view.slug)
      .then(setSelectedPost)
      .catch(console.error)
      .finally(() => setPostLoading(false));
  }, [view]);

  if (view.type === 'admin') {
    return (
      <div className="App">
        <AdminPage
          isAdmin={isAdmin}
          adminLoading={adminLoading}
          onNavigateHome={() => navigate('#/')}
        />
      </div>
    );
  }

  if (view.type === 'post') {
    if (postLoading || !selectedPost) {
      return (
        <div className="App">
          <div className="loading">{postLoading ? 'Loading…' : 'Post not found.'}</div>
        </div>
      );
    }
    return (
      <div className="App">
        <div className="blog-container">
          <SiteHeader />
          <button className="back-button" onClick={() => navigate('#/')}>
            ← Back to Posts
          </button>
          <BlogPost post={selectedPost} />
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BlogList
        posts={posts}
        loading={listLoading}
        onPostSelect={(slug) => navigate(`#/post/${slug}`)}
        onAdminClick={isAdmin ? () => navigate('#/admin') : undefined}
      />
    </div>
  );
}

export default App;
